/**
 * Language configuration for the Clinvea UI.
 *
 * Adding a language is a three-step job: add an entry here, create
 * `src/i18n/locales/<code>/` with one JSON file per namespace, and run
 * `npm run i18n:check` to confirm no key is missing. The parity test in
 * `src/i18n/__tests__/locale-parity.test.ts` fails the build if a namespace
 * is added for one language but not another.
 */

export const LANGUAGE_STORAGE_KEY = 'clinvea.language';
export const DEFAULT_LANGUAGE = 'en';

export interface SupportedLanguage {
  /** i18next language code — also the folder name under `locales/`. */
  code: string;
  /** Endonym: the language's name in its own script, which is what a speaker looks for. */
  label: string;
  /** English name, shown alongside the endonym so a non-reader can still identify the row. */
  englishLabel: string;
  /**
   * BCP-47 locale for Intl formatting.
   *
   * `-u-nu-latn` pins the numbering system to Latin digits (0-9). The CLDR default for
   * Telugu and Hindi is already `latn`, so today this is a no-op — it is here as a hard
   * guarantee that a future ICU/CLDR change can never start rendering a dose, a vital sign
   * or a date in Telugu (౦-౯) or Devanagari (०-९) numerals. See the rule in `clinicalSafety.ts`.
   */
  locale: string;
  /**
   * Script this language is written in. Drives which font stack the theme applies —
   * Inter carries no Indic glyphs, so a Telugu or Hindi string in Inter falls back to
   * whatever the OS has (or tofu boxes where it has nothing).
   */
  script: 'latin' | 'telugu' | 'devanagari';
}

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
  {
    code: 'en',
    label: 'English',
    englishLabel: 'English',
    locale: 'en-IN-u-nu-latn',
    script: 'latin',
  },
  {
    code: 'te',
    label: 'తెలుగు',
    englishLabel: 'Telugu',
    locale: 'te-IN-u-nu-latn',
    script: 'telugu',
  },
  {
    code: 'hi',
    label: 'हिन्दी',
    englishLabel: 'Hindi',
    locale: 'hi-IN-u-nu-latn',
    script: 'devanagari',
  },
] as const;

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export function getLanguage(code: string | undefined | null): SupportedLanguage {
  return (
    SUPPORTED_LANGUAGES.find((l) => l.code === code) ??
    SUPPORTED_LANGUAGES.find((l) => l.code === DEFAULT_LANGUAGE)!
  );
}

/**
 * Namespaces mirror the domain folders under `src/components/`, so the namespace a
 * component needs is the folder it lives in. They load on demand — a receptionist who
 * never opens the lab screens never downloads the `lab` bundle.
 *
 * `PRELOADED_NAMESPACES` are bundled into the initial chunk instead, because they render
 * before any route resolves; lazy-loading them would flash untranslated keys on first paint.
 */
export const NAMESPACES = [
  // Hand-authored and reviewed. The codemod is forbidden from writing to these.
  'common',
  'nav',
  'auth',
  'errors',
  'clinical',
  // Domain catalogues, extracted from the components by scripts/i18n-codemod.mjs.
  'admin',
  'appointments',
  'billing',
  'checkin',
  'consent',
  'consultation',
  'doctor',
  'inpatient',
  'insurance',
  'lab',
  'pages',
  'patients',
  'pharmacy',
  // Hand-authored: pharmacy purchasing, counter sales and reports (medopd-docs 142).
  'pharmacyPurchases',
  'pharmacySales',
  'pharmacyReports',
  // Lab front desk, result rules, report set-up (medopd-docs 151).
  'labFrontDesk',
  'labResults',
  'labReports',
  'labBusiness',
  // Tests & Prices (medopd-docs 153).
  'labTests',
  // Hand-authored: vendor work orders — dental lab, spectacle lenses, IOL (components/workOrders).
  'workOrders',
  // Hand-authored: treatment plans & estimates — dental + eye (components/treatment-plans).
  'treatmentPlans',
  // Hand-authored: eye care — eye exam, glasses prescriptions, cataract surgery, calculators (components/eyeCare).
  'eyeCare',
  // Hand-authored: optical shop — counter, lens-lab orders, stock, reports, My glasses (components/optical).
  'optical',
  // Hand-authored: dental care — tooth chart, gum chart, indices, braces, dental reports, "My teeth" (components/dental).
  'dental',
  // Hand-authored: procedure consent forms (components/consentForms) and interval recalls (components/recalls) — A4.
  'consentForms',
  'recalls',
  'portal',
  'prescriptions',
  'referrals',
  'reports',
  'staff',
  'ui',
  'vitals',
  'workspace',
  // Clinical images: X-rays, clinical photos, eye scans (dental + eye A3).
  'clinicalImages',
  // Hand-authored: "Arrange this page" on the role homes (components/dashboard).
  'dashboard',
  // Hand-authored: video consultations (components/video). Holds teleconsult consent wording.
  'video',
  // Hand-authored: in-place help — page explanations and the (i) beside fields (components/help, medopd-docs 97).
  'help',
  // Hand-authored: specialty consultation forms — renderer, builder, specialty packs (medopd-docs 115).
  'forms',
  // Hand-authored: first-login onboarding wizard (components/onboarding, medopd-docs 102).
  'onboarding',
  // Hand-authored: ClinVea Copilot panel, its tool activity lines and suggested questions (components/ai/copilot, medopd-docs 154).
  'copilot',
  // Hand-authored: Ask ClinVea, the patient portal's AI assistant, its notice, tool lines and questions (components/patient-portal/assistant).
  'patientAssistant',
  // Feedback built in plain code rather than JSX — toasts, confirm/alert, setError, thrown UI
  // errors — extracted by scripts/i18n-codemod-messages.mjs. Loaded at start-up (see
  // STARTUP_NAMESPACES): a toast can fire from a hook or lib helper on a screen that never
  // loaded the matching domain chunk, and an unloaded namespace renders raw keys.
  'messages',
  // Labels for API enum codes (statuses, roles, types), read through `enumLabel()` in
  // enumLabel.ts. Loaded at start-up for the same reason as `messages`: status chips and role
  // names render on every screen, from shared components that cannot know which domain chunk
  // happens to be loaded.
  'enums',
  // Server notifications rendered from their messageKey + params (notificationText.ts, medopd-docs 106).
  // Start-up: the bell is on every screen.
  'notifications',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const PRELOADED_NAMESPACES: Namespace[] = ['common', 'nav', 'auth', 'errors'];

/**
 * Requested at start-up but fetched through the lazy backend, so they stay out of the initial
 * bundle: they are never needed for first paint, only by the time someone clicks something.
 */
export const STARTUP_NAMESPACES: Namespace[] = [...PRELOADED_NAMESPACES, 'messages', 'enums', 'notifications'];

export const DEFAULT_NAMESPACE: Namespace = 'common';
